export function splitAssets(total: number, clientPercent: number) {
  // Use exact fractional math here so internal sums round-trip cleanly. In
  // particular, monthlyContributionAmount is stored as `yearly / 12` (already
  // fractional), so rounding the split would amplify the loss 12× when the form
  // re-derives the yearly value (e.g. $1000/yr split 50/50 was producing
  // $504/$496 instead of $500/$500). Display layers format with formatCurrency,
  // which handles dollar-rounding for presentation.
  const clientAmount = (total * clientPercent) / 100;
  const partnerAmount = total - clientAmount;
  return { clientAmount, partnerAmount };
}

export function splitFundingSourceFields(
  source: SelfFundingSource,
  clientPercent: number,
) {
  function split(value: number | null | undefined) {
    if (value == null) return null;
    return splitAssets(value, clientPercent);
  }

  const assets = split(source.existingAssetsValue);
  const contribution = split(source.monthlyContributionAmount);
  const income = split(source.monthlyIncome);
  const drawdown = split(source.drawdownAmount);

  return {
    clientOverrides: {
      ...(assets && { existingAssetsValue: assets.clientAmount }),
      ...(contribution && { monthlyContributionAmount: contribution.clientAmount }),
      ...(income && { monthlyIncome: income.clientAmount }),
      ...(drawdown && { drawdownAmount: drawdown.clientAmount }),
    },
    partnerOverrides: {
      ...(assets && { existingAssetsValue: assets.partnerAmount }),
      ...(contribution && { monthlyContributionAmount: contribution.partnerAmount }),
      ...(income && { monthlyIncome: income.partnerAmount }),
      ...(drawdown && { drawdownAmount: drawdown.partnerAmount }),
    },
  };
}

export function captureOriginalMonetaryValues(source: SelfFundingSource): Record<string, number> {
  const values: Record<string, number> = {};
  if (typeof source.existingAssetsValue === 'number') values.existingAssetsValue = source.existingAssetsValue;
  if (typeof source.monthlyContributionAmount === 'number') values.monthlyContributionAmount = source.monthlyContributionAmount;
  if (typeof source.monthlyIncome === 'number') values.monthlyIncome = source.monthlyIncome;
  if (typeof source.drawdownAmount === 'number') values.drawdownAmount = source.drawdownAmount;
  return values;
}

/**
 * Returns the pre-split combined totals for a self-funding source. Mirror sources
 * (splitRatio == null) hold the full amount on each side, so the source's own
 * values are the totals. Split sources hold each partner's portion, so we sum
 * — or, when the partner copy isn't available yet (e.g. cache hasn't refreshed),
 * we derive the total from `splitRatio` as a fallback.
 *
 * This is the single source of truth used by both the modal and guided planning
 * — never rely on session-stored originalMonetaryValues.
 */
export function computeCombinedTotals(
  source: SelfFundingSource | undefined,
  partnerSource: SelfFundingSource | undefined,
): Record<string, number> {
  if (!source) return {};

  // Mirror: each side holds the full amount, return source's own values.
  if (source.splitRatio == null) {
    return captureOriginalMonetaryValues(source);
  }

  const fields = [
    'existingAssetsValue',
    'monthlyContributionAmount',
    'monthlyIncome',
    'drawdownAmount',
  ] as const;

  // Split with partner copy available: sum the two sides (most accurate, no
  // rounding loss).
  if (partnerSource) {
    const result: Record<string, number> = {};
    fields.forEach(field => {
      const a = source[field];
      const b = partnerSource[field];
      if (typeof a === 'number' || typeof b === 'number') {
        result[field] = (typeof a === 'number' ? a : 0) + (typeof b === 'number' ? b : 0);
      }
    });
    return result;
  }

  // Split without partner copy: derive total from splitRatio. Refined when the
  // partner record refreshes; using fractional math so monthly amounts don't
  // amplify a rounding loss when the form re-derives the yearly value.
  const ratio = source.splitRatio > 0 ? source.splitRatio / 100 : null;
  if (ratio == null) return captureOriginalMonetaryValues(source);
  const result: Record<string, number> = {};
  fields.forEach(field => {
    const a = source[field];
    if (typeof a === 'number') {
      result[field] = a / ratio;
    }
  });
  return result;
}

export function findPartnerLinkedSource(
  partnerSelfFunding: Record<string, SelfFundingSource> | undefined,
  groupId: string,
): [string, SelfFundingSource] | undefined {
  if (!partnerSelfFunding) return undefined;
  return Object.entries(partnerSelfFunding).find(
    ([, ps]) => ps.fundingSourceGroupId === groupId,
  );
}

/**
 * Returns true when the given source was originated by the partner — i.e. the
 * current client is the recipient and should not be allowed to edit it. The
 * originator is identified by `addedByClientId`; older records without that
 * field can't be detected this way (callers should fall back to session
 * metadata for backward compatibility).
 */
export function isPartnerSharedRecipient(
  source: SelfFundingSource | undefined,
  currentClientId: string | undefined,
): boolean {
  if (!source || !currentClientId) return false;
  if (source.partnerAction !== 'mirrored') return false;
  if (!source.addedByClientId) return false;
  return source.addedByClientId !== currentClientId;
}
